import React, { FC, memo, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { PartialFiltersMatch } from 'components/HotelCard/PartialFiltersMatch/PartialFiltersMatch';
import { HotelCardContext } from 'components/HotelCardContext';
import { getOfferVariant } from 'components/Offer/Shared/utils';
import { OfferBox } from 'components/OfferBox/OfferBox';
import { PriceWatchCTAWrapper } from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper';
import { UnavailabilityMobile } from 'components/Unavailability/Mobile';
import { VacationRentalDetails } from 'components/VacationRentalDetails';
import { getHotel, getIsSearchPending } from 'modules/sapiSearch/selectors';
import { getAppLockedDealConfig, getHotelTopOffer, getOfferEntity } from 'modules/sapiSearch/selectors';
import { getListOfValues } from 'modules/search/selectors';
import { getIsOTAMode } from 'modules/search/selectors';
import { toggle } from 'opticks';
import { getRankedAvailableFacilities } from 'utils/facilities';
import { Box } from '@daedalus/atlas/helpers/Box';
import { OfferVariant } from '@daedalus/core/src/offer/types/OfferVariant';
import { PriceWatchBell } from '@daedalus/shared/src/priceWatch/components/PriceWatchBell';
import { Gallery } from '../Gallery/Gallery';
import { GalleryWithFetchPriority } from '../Gallery/GalleryWithFetchPriority';
import { HotelCardLayout } from '../HotelCardLayout/HotelCardLayout';
import { PropertySummary } from '../PropertySummary/PropertySummary';
const AvailabilityPaneWrapper = /*#__PURE__*/styled('div')({
  name: "AvailabilityPaneWrapper",
  class: "a19vmo95",
  propsAsIs: false
});
const SOURCE_COMPONENT = 'HotelCardMobile';

/**
 * Component responsible for composing components into `HotelCardLayout` slots
 */
const ComposedHotelCardComponent: FC<{
  isPriorityCard: boolean;
}> = ({
  isPriorityCard
}) => {
  const {
    hotelDetailsIsOpenOnLanding,
    hotelId,
    isAnchor
  } = useContext(HotelCardContext);
  const hotel = useSelector(state => getHotel(state, hotelId));
  const listOfValues = useSelector(getListOfValues);
  const rankedFacilities = getRankedAvailableFacilities(hotel?.facilities, listOfValues);
  const isOTAMode = useSelector(getIsOTAMode);
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId));
  const topOffer = useSelector(state => getHotelTopOffer(state, hotelId));
  const isSearchPending = useSelector(getIsSearchPending);
  const {
    isAppLockedDeal
  } = useSelector(state => getAppLockedDealConfig(state, hotelId, offerEntity?.id));
  const topOfferVariant = topOffer && getOfferVariant({
    isCheapest: false
  });
  const isTopOfferMemberDeal = topOfferVariant === OfferVariant.MemberDeal || isAppLockedDeal;
  const basicPropertyDetailsComponent = useMemo(() => hotelDetailsIsOpenOnLanding && isAnchor && <Box padding="s400">
          <VacationRentalDetails showVrDetails={hotel?.isVr} vrDetails={hotel?.vrDetails} rankedFacilities={rankedFacilities} />
        </Box>, [hotel, hotelDetailsIsOpenOnLanding, isAnchor, rankedFacilities]);
  const unavailabilityComponent = useMemo(() => <AvailabilityPaneWrapper className={cx(isTopOfferMemberDeal && `__specialBorder`)}>
        <UnavailabilityMobile hotelId={hotelId} sourceComponentPrefix={SOURCE_COMPONENT} />
      </AvailabilityPaneWrapper>, [hotelId, isTopOfferMemberDeal]);
  const filtersMatchSlot = useMemo(() => !isAnchor && <PartialFiltersMatch hasBasicPropertyDetails={hotelDetailsIsOpenOnLanding && isAnchor} />, [hotelDetailsIsOpenOnLanding, isAnchor]);

  // Added as part of feature-static-srp flag
  const isStaticSRPFeatureEnabled = toggle('feature-static-srp', false, true);
  return <HotelCardLayout isAnchor={isAnchor} isTopOfferMemberDeal={isTopOfferMemberDeal} gallerySlot={toggle('1ec91b00-mobile-hotel-card-lcp-image-fetchpriority', <Gallery isPriorityCard={isPriorityCard} />, <GalleryWithFetchPriority isPriorityCard={isPriorityCard} />)} propertySummarySlot={<PropertySummary hotelId={hotelId} />} watchButtonSlot={<PriceWatchCTAWrapper hotel={hotel} ctaComponent={PriceWatchBell} componentName="PriceWatchBell" searchIsCompleted={!isSearchPending} holdOutElement={null} />} basicPropertyDetailsSlot={basicPropertyDetailsComponent} filtersMatchSlot={filtersMatchSlot} offersSlot={!isStaticSRPFeatureEnabled && <OfferBox withHotelCard isLoading={isSearchPending} isOTAMode={isOTAMode} primaryOfferVariant="PrimaryMultiColumn" primaryOfferShouldShowTaxation={false} offerEntity={offerEntity} component={SOURCE_COMPONENT} isPrimaryOfferMemberDeal={isTopOfferMemberDeal} unavailabilitySlot={unavailabilityComponent} shouldRenderUnavailabilitySlot={isAnchor} />} />;
};
export const ComposedHotelCard = memo(ComposedHotelCardComponent);

require("./ComposedHotelCard.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ComposedHotelCard.tsx");